// apps/admin/src/components/monitoring/OnboardingHealthTab.tsx

import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import {
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  AlertCircle,
  BarChart3,
  RefreshCw
} from 'lucide-react';

interface OnboardingHealthMetrics {
  totalOnboardings: number;
  inProgress: number;
  completed: number;
  failed: number;
  successRate: number;

  last24Hours: {
    total: number;
    completed: number;
    failed: number;
    successRate: number;
  };

  last7Days: {
    total: number;
    completed: number;
    failed: number;
    successRate: number;
    avgDurationSeconds: number;
  };

  alerts: Array<{
    severity: 'warning' | 'error' | 'critical';
    type: 'stuck_onboarding' | 'high_failure_rate' | 'slow_processing' | 'data_anomaly';
    companyId: string;
    companyName: string;
    message: string;
    timestamp: Date;
    duration?: number;
  }>;

  stuckOnboardings: Array<{
    companyId: string;
    companyName: string;
    currentStage: string;
    progress: number;
    stuckDuration: number;
    lastActivity: Date;
  }>;

  failedOnboardings: Array<{
    companyId: string;
    companyName: string;
    failedStage: string;
    errorMessage: string;
    failedAt: Date;
    retryable: boolean;
  }>;

  avgCompletionTimeSeconds: number;
  p50CompletionTimeSeconds: number;
  p95CompletionTimeSeconds: number;
  p99CompletionTimeSeconds: number;

  stageFailures: Array<{
    stage: string;
    failureCount: number;
    commonErrors: string[];
  }>;

  trend: {
    successRateChange: number;
    avgDurationChange: number;
    failureRateChange: number;
  };
}

export const OnboardingHealthTab: React.FC = () => {
  const [health, setHealth] = useState<OnboardingHealthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchHealth = async () => {
    try {
      setLoading(true);
      setError(null);

      const getHealth = httpsCallable<void, OnboardingHealthMetrics>(
        functions,
        'getOnboardingHealth'
      );

      const result = await getHealth();
      setHealth(result.data);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Failed to fetch onboarding health:', err);
      setError(err.message || 'Failed to load health metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (seconds: number): string => {
    const rounded = Math.round(seconds);
    if (rounded < 60) return `${rounded}s`;
    const mins = Math.floor(rounded / 60);
    const secs = rounded % 60;
    return `${mins}m ${secs}s`;
  };

  const getPerformanceStatus = (seconds: number): { color: string; label: string; bg: string } => {
    if (seconds < 120) return { color: 'text-green-400', label: 'Excellent', bg: 'bg-green-500/20' };
    if (seconds < 300) return { color: 'text-yellow-400', label: 'Good', bg: 'bg-yellow-500/20' };
    if (seconds < 600) return { color: 'text-orange-400', label: 'Slow', bg: 'bg-orange-500/20' };
    return { color: 'text-red-400', label: 'Critical', bg: 'bg-red-500/20' };
  };

  const getSeverityColor = (severity: 'warning' | 'error' | 'critical') => {
    switch (severity) {
      case 'critical': return 'border-red-500/50 bg-red-500/10';
      case 'error': return 'border-orange-500/50 bg-orange-500/10';
      case 'warning': return 'border-yellow-500/50 bg-yellow-500/10';
    }
  };

  const getSeverityIcon = (severity: 'warning' | 'error' | 'critical') => {
    switch (severity) {
      case 'critical': return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'error': return <XCircle className="w-5 h-5 text-orange-400" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
    }
  };

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
          <div className="text-slate-400">Loading onboarding health metrics...</div>
        </div>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <XCircle className="w-6 h-6 text-red-400" />
          <h3 className="text-lg font-semibold text-red-300">Failed to Load Health Metrics</h3>
        </div>
        <p className="text-sm text-red-300 mb-4">{error}</p>
        <button
          onClick={fetchHealth}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!health) return null;

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-400">
          Last updated: {lastRefresh.toLocaleTimeString()}
        </div>
        <button
          onClick={fetchHealth}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Total</p>
              <p className="text-2xl font-bold text-white">{health.totalOnboardings}</p>
            </div>
            <Activity className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">In Progress</p>
              <p className="text-2xl font-bold text-yellow-400">{health.inProgress}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Completed</p>
              <p className="text-2xl font-bold text-green-400">{health.completed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Failed</p>
              <p className="text-2xl font-bold text-red-400">{health.failed}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Success Rate Card */}
      <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-green-300 mb-1">Overall Success Rate</p>
            <p className="text-4xl font-bold text-white">{health.successRate}%</p>
          </div>
          <TrendingUp className="w-12 h-12 text-green-400" />
        </div>
      </div>

      {/* Active Alerts */}
      {health.alerts.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Active Alerts ({health.alerts.length})
            </h2>
          </div>
          <div className="divide-y divide-slate-700">
            {health.alerts.map((alert, idx) => (
              <div key={idx} className={`p-4 border-l-4 ${getSeverityColor(alert.severity)}`}>
                <div className="flex items-start gap-3">
                  {getSeverityIcon(alert.severity)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-white">{alert.companyName}</span>
                      <span className="text-xs text-slate-500 uppercase px-2 py-0.5 bg-slate-700 rounded">
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 mb-2">{alert.message}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>Type: {alert.type}</span>
                      {alert.duration && <span>Duration: {formatDuration(alert.duration)}</span>}
                    </div>
                  </div>
                  <a
                    href={`/companies/onboarding-status/${alert.companyId}`}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    View Details
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time-Based Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Last 24 Hours */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Last 24 Hours
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">Total:</span>
              <span className="text-white font-medium">{health.last24Hours.total}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Completed:</span>
              <span className="text-green-400 font-medium">{health.last24Hours.completed}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Failed:</span>
              <span className="text-red-400 font-medium">{health.last24Hours.failed}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-slate-700">
              <span className="text-slate-400">Success Rate:</span>
              <span className="text-white font-bold">{health.last24Hours.successRate}%</span>
            </div>
          </div>
        </div>

        {/* Last 7 Days */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-400" />
            Last 7 Days
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">Total:</span>
              <span className="text-white font-medium">{health.last7Days.total}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Completed:</span>
              <span className="text-green-400 font-medium">{health.last7Days.completed}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Failed:</span>
              <span className="text-red-400 font-medium">{health.last7Days.failed}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Avg Duration:</span>
              <span className="text-blue-400 font-medium">
                {formatDuration(health.last7Days.avgDurationSeconds)}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t border-slate-700">
              <span className="text-slate-400">Success Rate:</span>
              <span className="text-white font-bold">{health.last7Days.successRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stuck Onboardings */}
      {health.stuckOnboardings.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              Stuck Onboardings ({health.stuckOnboardings.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Stage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Progress</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Stuck For</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {health.stuckOnboardings.map((stuck, idx) => (
                  <tr key={idx} className="hover:bg-slate-700/20">
                    <td className="px-4 py-3 text-sm text-white">{stuck.companyName}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{stuck.currentStage}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{stuck.progress}%</td>
                    <td className="px-4 py-3 text-sm text-yellow-400">{stuck.stuckDuration} min</td>
                    <td className="px-4 py-3">
                      <a
                        href={`/companies/onboarding-status/${stuck.companyId}`}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                      >
                        View Details
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Failed Onboardings */}
      {health.failedOnboardings.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Failed Onboardings ({health.failedOnboardings.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Failed Stage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Error</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Retryable</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {health.failedOnboardings.map((failed, idx) => (
                  <tr key={idx} className="hover:bg-slate-700/20">
                    <td className="px-4 py-3 text-sm text-white">{failed.companyName}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{failed.failedStage}</td>
                    <td className="px-4 py-3 text-sm text-red-300 max-w-xs truncate" title={failed.errorMessage}>
                      {failed.errorMessage}
                    </td>
                    <td className="px-4 py-3">
                      {failed.retryable ? (
                        <span className="text-xs px-2 py-1 bg-green-500/20 text-green-300 rounded">Yes</span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-red-500/20 text-red-300 rounded">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={`/companies/onboarding-status/${failed.companyId}`}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                      >
                        View Details
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-400" />
          Performance Metrics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Average */}
          {(() => {
            const status = getPerformanceStatus(health.avgCompletionTimeSeconds);
            return (
              <div className={`rounded-lg border border-slate-700 p-3 ${status.bg}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-400">Average</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${status.bg} ${status.color} font-medium`}>
                    {status.label}
                  </span>
                </div>
                <p className={`text-2xl font-bold ${status.color}`}>
                  {formatDuration(health.avgCompletionTimeSeconds)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Mean completion time</p>
              </div>
            );
          })()}

          {/* P50 (Median) */}
          {(() => {
            const status = getPerformanceStatus(health.p50CompletionTimeSeconds);
            return (
              <div className={`rounded-lg border border-slate-700 p-3 ${status.bg}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-400">P50 (Median)</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${status.bg} ${status.color} font-medium`}>
                    {status.label}
                  </span>
                </div>
                <p className={`text-2xl font-bold ${status.color}`}>
                  {formatDuration(health.p50CompletionTimeSeconds)}
                </p>
                <p className="text-xs text-slate-500 mt-1">50% complete faster</p>
              </div>
            );
          })()}

          {/* P95 */}
          {(() => {
            const status = getPerformanceStatus(health.p95CompletionTimeSeconds);
            return (
              <div className={`rounded-lg border border-slate-700 p-3 ${status.bg}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-400">P95</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${status.bg} ${status.color} font-medium`}>
                    {status.label}
                  </span>
                </div>
                <p className={`text-2xl font-bold ${status.color}`}>
                  {formatDuration(health.p95CompletionTimeSeconds)}
                </p>
                <p className="text-xs text-slate-500 mt-1">95% complete faster</p>
              </div>
            );
          })()}

          {/* P99 */}
          {(() => {
            const status = getPerformanceStatus(health.p99CompletionTimeSeconds);
            return (
              <div className={`rounded-lg border border-slate-700 p-3 ${status.bg}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-400">P99</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${status.bg} ${status.color} font-medium`}>
                    {status.label}
                  </span>
                </div>
                <p className={`text-2xl font-bold ${status.color}`}>
                  {formatDuration(health.p99CompletionTimeSeconds)}
                </p>
                <p className="text-xs text-slate-500 mt-1">99% complete faster (worst case)</p>
              </div>
            );
          })()}
        </div>

        {/* Performance Benchmarks Legend */}
        <div className="mt-4 pt-4 border-t border-slate-700">
          <p className="text-xs text-slate-400 mb-2 font-medium">Performance Benchmarks:</p>
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/50"></div>
              <span className="text-slate-400"><span className="text-green-400 font-medium">Excellent</span> - Under 2 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-yellow-500/20 border border-yellow-500/50"></div>
              <span className="text-slate-400"><span className="text-yellow-400 font-medium">Good</span> - 2-5 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-orange-500/20 border border-orange-500/50"></div>
              <span className="text-slate-400"><span className="text-orange-400 font-medium">Slow</span> - 5-10 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-red-500/20 border border-red-500/50"></div>
              <span className="text-slate-400"><span className="text-red-400 font-medium">Critical</span> - Over 10 minutes</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stage Failures */}
      {health.stageFailures.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Common Failure Points
            </h2>
          </div>
          <div className="p-4 space-y-4">
            {health.stageFailures.map((failure, idx) => (
              <div key={idx} className="border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-white">{failure.stage}</h3>
                  <span className="text-sm text-red-400">{failure.failureCount} failures</span>
                </div>
                <ul className="space-y-1">
                  {failure.commonErrors.map((error, i) => (
                    <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>{error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <p className="text-sm text-blue-300">
          <strong>Google SRE Monitoring:</strong> This dashboard provides aggregate health metrics
          for all {health.totalOnboardings.toLocaleString()} company onboardings without individual log checking.
          Auto-refreshes every 30 seconds. Alerts are prioritized by severity for proactive issue detection.
        </p>
      </div>
    </div>
  );
};
